import React, { useEffect, useRef, useState } from 'react';
import { getApiUrl } from '../config';
import '../styles/components/DependencyGraph.css';
import ForceGraph2D from 'react-force-graph-2d';
import { useResizeDetector } from 'react-resize-detector';

const DependencyGraph = ({ telegramId }) => {
  const [graphData, setGraphData] = useState({ nodes: [], links: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const graphRef = useRef(null);
  const { width, height, ref } = useResizeDetector();

  useEffect(() => {
    const fetchGraphData = async () => {
      if (!telegramId) return;

      try {
        setLoading(true);
        setError(null);

        const formData = new FormData();
        formData.append('telegram_id', telegramId);

        const response = await fetch(getApiUrl('/api/users_dependency_graph'), {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          throw new Error(`Ошибка при получении данных графа: ${response.status}`);
        }

        const data = await response.json();
        
        if (data.success) {
          // Преобразуем данные в формат для компонента Force Graph
          const graphData = {
            nodes: data.nodes.map(node => ({
              id: node.id,
              name: node.name || node.username || `User ${node.id}`,
              avatar: node.avatar,
              telegram_id: node.telegram_id,
              // ** Дизайн узла: Цвет **
              // Цвет узла зависит от того, есть ли у пользователя приглашенные:
              // '#3498db' (синий) - если есть приглашенные,
              // '#e74c3c' (красный) - если нет приглашенных.
              // Вы можете изменить эти цвета на любые другие в формате HEX (например, '#FF0000' для красного).
              color: node.invited_users.length > 0 ? '#1a659e' : '#c53448',
              // ** Дизайн узла: Размер **
              // Размер узла зависит от количества приглашенных пользователей.
              // Базовое значение = 1. К нему прибавляется 0.5 за каждого приглашенного.
              // Этот параметр (`val`) затем используется для вычисления радиуса круга узла.
              // Можно изменить множитель (0.5) или базовое значение (1), чтобы влиять на разницу в размерах узлов.
              val: 1 + (node.invited_users.length * 0.5) // Размер узла зависит от количества приглашенных
            })),
            links: data.links.map(link => ({
              source: link.source,
              target: link.target,
              value: 1
            }))
          };
          setGraphData(graphData);
        } else {
          throw new Error(data.error || 'Не удалось получить данные графа');
        }
      } catch (err) {
        console.error('Ошибка при загрузке графа:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchGraphData();
  }, [telegramId]);

  // ** Функция отрисовки узла (NodeObject) **
  // Эта функция отвечает за то, как каждый узел будет выглядеть на графе.
  const NodeObject = ({ node, ctx }) => {
    // ** Дизайн узла: Размер круга **
    // Рассчитываем радиус круга узла (NODE_R).
    // Базовый радиус = 4 пикселя. К нему прибавляется значение 'val', рассчитанное ранее (зависит от числа приглашенных).
    // Увеличивая '4' или изменяя расчет 'val', можно менять общий размер узлов и разницу между ними.
    const NODE_R = 4 + node.val;
    
    // Рисуем круг узла
    ctx.beginPath();
    // ** Дизайн узла: Цвет круга **
    // Используем цвет, определенный ранее для узла (`node.color`).
    ctx.fillStyle = node.color;
    // Рисуем сам круг с центром в (node.x, node.y) и радиусом NODE_R.
    ctx.arc(node.x, node.y, NODE_R, 0, 2 * Math.PI);
    ctx.fill();
    
    // Рисуем имя пользователя под кругом
    // ** Дизайн узла: Цвет текста **
    // Устанавливаем цвет текста имени. Можно изменить на любой цвет (например, 'white', '#cccccc').
    ctx.fillStyle = 'black';
    // ** Дизайн узла: Шрифт и размер текста **
    // Устанавливаем размер и тип шрифта для имени. '6px Arial' - шрифт Arial размером 6 пикселей.
    // Можно изменить размер (например, '8px') или шрифт (например, 'Verdana').
    ctx.font = '6px Arial';
    ctx.textAlign = 'center'; // Выравнивание текста по центру
    ctx.textBaseline = 'middle'; // Выравнивание текста по вертикали
    // ** Дизайн узла: Позиция текста **
    // Выводим текст (`node.name`) ниже круга.
    // node.x - позиция по горизонтали (центр круга).
    // node.y + NODE_R + 6 - позиция по вертикали (ниже круга на 6 пикселей + радиус круга).
    // Можно изменить '6' для регулировки отступа текста от круга.
    ctx.fillText(node.name, node.x, node.y + NODE_R + 6);
  };

  if (loading) {
    return (
      <div className="dependency-graph-container">
        <div className="loading-indicator">
          <div className="spinner"></div>
          <p>Загрузка графа зависимостей...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dependency-graph-container">
        <div className="error-container">
          <h3>Ошибка при загрузке графа</h3>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Попробовать снова</button>
        </div>
      </div>
    );
  }

  return (
    <div className="dependency-graph-container" ref={ref}>
      {width && height && (
        <ForceGraph2D
          ref={graphRef}
          graphData={graphData}
          width={width}
          height={height}
          nodeLabel={node => `${node.name}`}
          nodeCanvasObject={(node, ctx) => NodeObject({ node, ctx })}
          // ** Дизайн связей: Цвет **
          // Устанавливает цвет связей между узлами.
          // 'rgba(0, 0, 0, 0.2)' - полупрозрачный черный цвет.
          // Можно изменить на любой другой цвет в формате RGBA или HEX.
          linkColor={() => 'rgba(0, 0, 0, 0.2)'}
          // ** Дизайн связей: Толщина **
          // Устанавливает толщину линий связей в пикселях.
          linkWidth={1}
          // ** Параметры физики графа **
          // Эти параметры влияют на то, как узлы "располагаются" и "успокаиваются".
          // Уменьшение `d3AlphaDecay` сделает симуляцию дольше, узлы будут двигаться активнее.
          // Увеличение сделает ее быстрее. Значение по умолчанию 0.0228.
          d3AlphaDecay={0.02}
          // `d3VelocityDecay` - это коэффициент "трения". От 0 до 1.
          // Большее значение (ближе к 1) означает меньшее трение, узлы будут двигаться дольше.
          // Меньшее значение (ближе к 0) означает большее трение, узлы остановятся быстрее. Значение по умолчанию 0.4.
          d3VelocityDecay={0.2}
          // `cooldownTime` - время в миллисекундах (5000 = 5 секунд), после которого симуляция останавливается,
          // если граф стабилизировался. Установка в Infinity заставит граф двигаться постоянно.
          cooldownTime={5000}
          onNodeClick={(node) => {
            // При клике на узел можно открыть профиль пользователя
            console.log(`Выбран пользователь: ${node.name} (ID: ${node.telegram_id})`);
          }}
        />
      )}
    </div>
  );
};

export default DependencyGraph; 